'use client';

import React, { useCallback, useEffect, useState } from 'react';

import { isChunkLoadFailure, requestChunkRecoveryReload } from '@/lib/chunkRecovery';
import {
  PortalRecoveryError,
  PortalRecoverySource,
  reportPortalRecovery,
} from '@/lib/landingBeacon';

interface IPortalRecoveryProps {
  onRetry: () => void;
  source: PortalRecoverySource;
  /** What the boundary caught. Without it this screen is a crash counted and never identified. */
  error?: PortalRecoveryError;
}

export const PortalRecovery = ({ onRetry, source, error }: IPortalRecoveryProps): React.ReactElement => {
  // A chunk failure that a boundary catches never reaches the window handlers
  // in public/chunk-recovery.js, so the boundary spends the same one-per-session
  // reload itself: a fresh document points at the current deploy's chunks, which
  // fixes the usual cause outright. Only when the allowance is already spent —
  // this IS the reloaded document, still failing — does the player see the screen.
  const [isReloading, setIsReloading] = useState(() => isChunkLoadFailure(error));

  useEffect(() => {
    if (isChunkLoadFailure(error) && requestChunkRecoveryReload()) {
      reportPortalRecovery(source, 'reload-dispatched', error);
      return;
    }

    setIsReloading(false);
    reportPortalRecovery(source, 'fallback-displayed', error);
  }, [source, error]);

  const handleRetry = useCallback((): void => {
    reportPortalRecovery(source, 'retry-selected');
    onRetry();
  }, [onRetry, source]);

  const handlePortalNavigation = useCallback((): void => {
    reportPortalRecovery(source, 'portal-selected');
    window.location.assign('/');
  }, [source]);

  if (isReloading) {
    // The reload lands in ~150ms; flashing the crash copy first would make a
    // recovered player think something broke.
    return (
      <main
        data-testid="portal-recovery-reloading"
        style={{ background: '#0b1020', minHeight: '100vh' }}
      />
    );
  }

  return (
    <main
      data-testid="portal-recovery"
      style={{
        alignItems: 'center',
        background: '#0b1020',
        color: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Arial, sans-serif',
        gap: '16px',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      <h1>We could not load the portal</h1>
      <p style={{ margin: 0, maxWidth: '512px' }}>Reload the portal to continue.</p>
      <div style={{ alignItems: 'center', display: 'flex', gap: '16px' }}>
        <button
          type="button"
          onClick={handleRetry}
          style={{
            background: '#ffffff',
            border: 0,
            borderRadius: '8px',
            color: '#0b1020',
            cursor: 'pointer',
            fontWeight: 600,
            padding: '12px 20px',
          }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={handlePortalNavigation}
          style={{
            background: 'transparent',
            border: 0,
            color: '#ffffff',
            cursor: 'pointer',
            fontWeight: 600,
            padding: '12px',
            textDecoration: 'underline',
          }}
        >
          Go to portal
        </button>
      </div>
    </main>
  );
};
